/**
 * The human-in-the-loop (HITL) contract shared by the agent backend that RAISES
 * an interrupt and the chat frontend that RENDERS the resulting card. Both sides
 * reference these enums instead of re-declaring `'commit-approval'` / `'normal'`
 * / `'confirmed'` string unions, so the wire discriminants can never drift
 * between producer and consumer.
 */

/**
 * Every HITL interrupt kind the agent can raise — that is, every card that
 * SUSPENDS the turn until a human settles it. Approval kinds (`CommitApproval`,
 * `SendApproval`) require a signed token; `Clarification` is resolved by a user
 * reply without one.
 *
 * The navigation link is deliberately absent: it is rendered beside a delivered
 * answer and blocks nothing, so it has no interrupt to be a kind of. Its click
 * still reaches the agent — as the next message, through
 * `ChatResume.navigateDecision`.
 */
export enum InterruptKind {
  CommitApproval = 'commit-approval',
  SendApproval = 'send-approval',
  Clarification = 'clarification',
}

/**
 * The interrupt kinds that surface as a Confirm card (vs. the question card) and
 * that the gate mints a signed token for: `CommitApproval` → the contract plan
 * card, `SendApproval` → the email card.
 */
export type ApprovalKind =
  | InterruptKind.CommitApproval
  | InterruptKind.SendApproval;

/**
 * How firmly the card makes the human commit.
 *
 * - `Normal`: Confirm dispatches the resume immediately.
 * - `Strong`: the action is irreversible — Confirm starts a grace countdown and
 *   only dispatches when it elapses.
 */
export enum ApprovalStrength {
  Normal = 'normal',
  Strong = 'strong',
}

/**
 * The recorded human decision, persisted onto the approval part once made so a
 * reloaded card renders exactly as it did live.
 */
export enum ApprovalDecisionState {
  Confirmed = 'confirmed',
  Cancelled = 'cancelled',
}
